import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip';
import { PartyType } from 'types/contacts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { ContactFormState } from './useContactForm';

/** The keyword multi-select: under Other for a company, beside Areas of Responsibility for a person. */
export const RelatedKeywordsInput = ({ form }: { form: ContactFormState }): JSX.Element => (
    <MultiSelectInput
        control={form.methods.control}
        name="relatedKeywords"
        label="contacts.related-keywords"
        placeholder="contacts.related-keywords"
        options={form.relatedKeywordOptions}
        setValue={form.methods.setValue}
        getOptionLabel="label"
        getOptionIdentifier="value"
        sortDirection="asc"
    />
);

/** The reason the relationship-growth keyword requires, drawn under the keyword field once it is picked. */
export const RelationshipGrowthNotesInput = ({ form }: { form: ContactFormState }): JSX.Element | null =>
    form.relationshipGrowthKeywordIndex > -1 ? (
        <TextAreaInput
            name={`relatedKeywords.${form.relationshipGrowthKeywordIndex}.notes`}
            control={form.methods.control}
            label="contacts.reason-for-relationship-growth-potential"
            required
        />
    ) : null;

/** Other: politically exposed person, DMS link, notes, a company's keywords and confirmed cooperation areas. */
export const OtherFields = ({ form }: { form: ContactFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers, isUserPrivilegedToUpdateDmsLink, partyType } = form;

    return (
        <>
            {partyType === PartyType.PERSON ? (
                <CheckboxInput
                    control={control}
                    name="isPoliticallyExposedPerson"
                    label="contacts.politically-exposed-person"
                />
            ) : null}
            <TextInput
                name="dmsLink"
                control={control}
                label="contacts.dms-link"
                disabled={!isUserPrivilegedToUpdateDmsLink}
            />
            <TextAreaInput name="notes" control={control} label="contacts.other.notes" enableAutofill />
            <TextAreaInput name="coiNotes" control={control} label="contacts.other.coi-notes" enableAutofill />
            {partyType === PartyType.PERSON ? null : (
                <>
                    <RelatedKeywordsInput form={form} />
                    <RelationshipGrowthNotesInput form={form} />
                </>
            )}
            <MultiSelectInput
                control={control}
                name="cooperationAreaTypes"
                label="contacts.other.confirmed-cooperation-areas"
                placeholder="contacts.other.confirmed-cooperation-areas"
                tooltip={<InfoTooltip text="contacts.other.tooltips.confirmed-cooperation-areas" />}
                options={getValidOptionsFromClassifier<string, string>(classifiers['COOPERATION_AREA_TYPE']) ?? []}
                setValue={setValue}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
            />
        </>
    );
};
